import React from 'react';
import { Typography, Box, List, ListItem, ListItemText, Alert } from '@mui/material';
import LegalLayout from './LegalLayout';

const ReturnPolicy: React.FC = () => {
  return (
    <LegalLayout title="Политика за връщане">
      <Alert severity="info" sx={{ mb: 3 }}>
        Всички връщания се извършват в съответствие със Закона за защита на потребителите 
        и европейското законодателство за защита на потребителите.
      </Alert>

      <Typography variant="h5" component="h2" gutterBottom>
        1. Право на връщане
      </Typography>
      <Typography paragraph>
        Като потребител имате право да върнете закупените продукти без да посочвате 
        причина в следните срокове:
      </Typography>
      <List>
        <ListItem>
          <ListItemText 
            primary="14 дни за всички продукти" 
            secondary="Съгласно европейското законодателство за защита на потребителите" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="3 дни за книги" 
            secondary="Специален срок за книги поради тяхната специфика" 
          />
        </ListItem>
      </List>
      <Typography paragraph>
        Срокът се брои от деня, в който Вие или посочено от Вас лице (различно от 
        превозвача) сте получили продукта.
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        2. Условия за връщане
      </Typography>
      <Typography paragraph>
        За да върнете продукт, той трябва да отговаря на следните условия:
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Да бъде в оригинално състояние" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Да не е използван или повреден" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Да е в оригиналната опаковка (ако има такава)" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Да съдържа всички аксесоари и документи" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Да има запазен етикет и баркод" />
        </ListItem>
      </List>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        3. Продукти, които не подлежат на връщане
      </Typography>
      <Typography paragraph>
        Следните продукти не могат да бъдат върнати поради хигиенни причини или 
        специфичния им характер:
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Персонализирани или поръчани специално продукти" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Продукти с нарушена хигиенна опаковка" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Продукти, които се развалят бързо" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Цифрови продукти (електронни книги, аудиокниги)" />
        </ListItem>
      </List>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        4. Процедура за връщане
      </Typography>
      <Typography paragraph>
        За да върнете продукт, следвайте тези стъпки:
      </Typography>
      <Box component="ol" sx={{ pl: 2 }}>
        <Typography component="li" paragraph>
          <strong>Свържете се с нас</strong> - Изпратете имейл до info@goldenbooks.bg 
          или се обадете на 0895628293 в работно време (9:00-18:00 ч.)
        </Typography>
        <Typography component="li" paragraph>
          <strong>Посочете причината</strong> - Опишете причината за връщането 
          (грешка в поръчката, дефект и др.)
        </Typography>
        <Typography component="li" paragraph>
          <strong>Получете инструкции</strong> - Ще Ви изпратим подробни инструкции 
          и адрес за връщане на продукта
        </Typography>
        <Typography component="li" paragraph>
          <strong>Опаковайте продукта</strong> - Опаковайте внимателно продукта в 
          оригиналната опаковка или подходяща алтернатива
        </Typography>
        <Typography component="li" paragraph>
          <strong>Изпратете продукта</strong> - Изпратете продукта на посочения адрес 
          чрез куриерска фирма или поща
        </Typography>
      </Box>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        5. Разходи за връщане
      </Typography>
      <Typography paragraph>
        Разходите за връщане на продукта се поемат според следните правила:
      </Typography>
      <List>
        <ListItem>
          <ListItemText 
            primary="От Вас" 
            secondary="Ако връщате продукта, защото не Ви харесва или сте променили решението си" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="От нас" 
            secondary="Ако продуктът е дефектен, повреден или сме изпратили грешен артикул" 
          />
        </ListItem>
      </List>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        6. Възстановяване на средствата
      </Typography>
      <Typography paragraph>
        След като получим и проверим върнатия продукт:
      </Typography>
      <List>
        <ListItem>
          <ListItemText 
            primary="Срок за възстановяване" 
            secondary="30 календарни дни от получаване на продукта"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Начин на възстановяване" 
            secondary="По същия начин, по който е направено плащането" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Банков превод" 
            secondary="Необходимите работни дни за постъпване в сметката Ви"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Кредитна карта" 
            secondary="Необходимите работни дни според банката Ви"
          />
        </ListItem>
      </List>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        7. Дефектни или повредени продукти
      </Typography>
      <Typography paragraph>
        Ако получите дефектен или повреден продукт:
      </Typography>
      <List>
        <ListItem>
          <ListItemText primary="Свържете се с нас незабавно (в рамките на 48 часа)" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Опишете проблема и изпратете снимки, ако е възможно" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Ще организираме безплатно прибиране на продукта" />
        </ListItem>
        <ListItem>
          <ListItemText primary="Ще предложим замяна или пълно възстановяване" />
        </ListItem>
      </List>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        8. Гаранция
      </Typography>
      <Typography paragraph>
        Всички продукти имат гаранция срещу производствени дефекти съгласно 
        българското законодателство. Гаранционният срок е:
      </Typography>
      <List>
        <ListItem>
          <ListItemText 
            primary="2 години" 
            secondary="За електронни и технически продукти" 
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Според производителя" 
            secondary="За книги и други печатни материали - проверка за печатни грешки" 
          />
        </ListItem>
      </List>

      <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
        9. Контакт за въпроси
      </Typography>
      <Typography paragraph>
        За въпроси относно връщанията и рекламациите:
      </Typography>
      <Typography paragraph>
        <strong>"Голдън Букс" ЕООД</strong><br />
        Имейл: info@goldenbooks.bg<br />
        Телефон: 0895628293<br />
        Работно време: 9:00 - 18:00 ч. (работни дни)<br />
        Адрес: София, България
      </Typography>
      
      <Alert severity="success" sx={{ mt: 3 }}>
        Вашето удовлетворение е наш приоритет! При всякакви проблеми с продуктите 
        ни се свържете с нас и ще намерим най-доброто решение.
      </Alert>
    </LegalLayout>
  );
};

export default ReturnPolicy;